import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useMemo, useState} from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  MOON_600,
  MOON_800,
} from '../../../../../../../../common/css/color.styles';
import {
  formatNumericTick,
  formatTooltipValue,
  truncateText,
} from '../../../../charts/format';
import {PLOT_GRID_COLOR} from '../../ecpConstants';
import {MetricMetadata} from './RechartsRadarChart';

const CONSTANTS = {
  AXIS_WIDTH: 40,
  NAME_MAX_LEN: 40,
  FONT_SIZE: 10,
  TITLE_FONT_SIZE: 12,
  MARGIN: {top: 10, right: 20, bottom: 20, left: 0},
  BAR_GAP: 0.1,
  TOOLTIP_WIDTH: 230,
  TOOLTIP_PAD_X: 12,
  TOOLTIP_PAD_Y: 8,
  COLOR_BOX: 'w-12 h-12',
  GAP: 'gap-8',
};

export type BarChartData = {
  x: string[];
  y: number[];
  colors: string[];
  names: string[];
  name: string;
  yRange: [number, number];
  metadata?: MetricMetadata;
};

const BarTooltip = ({active, payload, metadata}: any) => {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <Tailwind>
      <div
        className="shadow-lg rounded bg-[#333] text-white text-xs"
        style={{
          width: CONSTANTS.TOOLTIP_WIDTH,
          padding: `${CONSTANTS.TOOLTIP_PAD_Y}px ${CONSTANTS.TOOLTIP_PAD_X}px`,
          transform: 'translate(-100%, -100%)',
        }}>
        <div className="font-bold mb-6">
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            {truncateText(data.name, CONSTANTS.NAME_MAX_LEN)}
          </div>
        </div>
        <div className={`flex items-center ${CONSTANTS.GAP} mb-2`}>
          <div
            className={`rounded-sm shrink-0 ${CONSTANTS.COLOR_BOX}`}
            style={{backgroundColor: data.color}}
          />
          <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
            Value:
          </div>
          <div className="font-bold font-mono shrink-0">
            {formatTooltipValue(
              data.value,
              metadata?.scoreType === 'binary' ? '%' : metadata?.unit
            )}
          </div>
        </div>
      </div>
    </Tailwind>
  );
};

export const RechartsBarChart: React.FC<{
  data: BarChartData;
}> = ({data}) => {
  const [tooltipPos, setTooltipPos] = useState<
    {x: number; y: number} | undefined
  >(undefined);

  const chartData = useMemo(() => {
    return data.x.map((label, i) => ({
      label,
      value: data.y[i],
      color: data.colors[i],
      name: data.names?.[i] ?? label,
    }));
  }, [data]);

  const yAxisFormatter = useMemo(() => {
    return (value: number) => {
      if (data.metadata?.scoreType === 'binary') {
        const pct = value <= 1 ? value * 100 : value;
        return `${pct.toFixed(0)}%`;
      }
      return formatNumericTick(value);
    };
  }, [data.metadata]);

  const yDomain = useMemo(() => {
    if (data.metadata?.scoreType === 'binary') {
      return [0, 1];
    }
    return data.yRange;
  }, [data.metadata, data.yRange]);

  if (!chartData.length) return <div>No data available</div>;

  return (
    <>
      <style>{`.recharts-wrapper * { outline: none !important; }`}</style>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={CONSTANTS.MARGIN}
          barGap={CONSTANTS.BAR_GAP}
          onMouseMove={(state: any) => {
            if (state.isTooltipActive) {
              setTooltipPos({
                x: state.chartX - 10,
                y: state.chartY - 10,
              });
            } else {
              setTooltipPos(undefined);
            }
          }}
          onMouseLeave={() => setTooltipPos(undefined)}>
          <CartesianGrid
            stroke={PLOT_GRID_COLOR}
            horizontal={false}
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{fontSize: CONSTANTS.FONT_SIZE}}
            tickLine={false}
            axisLine={{stroke: PLOT_GRID_COLOR}}
            hide={false}
            tickFormatter={() => ''}
            height={1}
          />
          <YAxis
            domain={yDomain}
            tick={{fontSize: CONSTANTS.FONT_SIZE, fill: MOON_600}}
            tickLine={false}
            stroke={PLOT_GRID_COLOR}
            tickFormatter={yAxisFormatter}
            width={CONSTANTS.AXIS_WIDTH}
          />
          <Bar dataKey="value" isAnimationActive={false}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
            <LabelList
              dataKey="value"
              position="top"
              formatter={yAxisFormatter}
              style={{fontSize: 14, fill: MOON_800}}
            />
          </Bar>
          <Tooltip
            wrapperClassName="pointer-events-none"
            isAnimationActive={false}
            cursor={{fill: '#000', opacity: 0.04, radius: 4}}
            position={tooltipPos}
            content={(props: any) => (
              <BarTooltip {...props} metadata={data.metadata} />
            )}
          />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};
